import React, { useState, useEffect } from 'react';
import {
    getAnalyticsKpi, getPredictions, getRisques, getRecommandations,
    getTendances, getAnalyticsClassement, getAnalyticsOverview, getPredictionsEnriched,
    getEquipes
} from '../services/api';
import Chart from 'react-apexcharts';
import {
    FiTrendingUp, FiTrendingDown, FiAlertTriangle, FiZap, FiTarget,
    FiAward, FiUsers, FiActivity, FiDollarSign, FiSearch,
    FiChevronUp, FiChevronDown, FiStar, FiAlertCircle, FiRefreshCw,
    FiCalendar, FiBarChart2, FiCheckCircle, FiClock
} from 'react-icons/fi';

const JOURS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

const CAT_CONFIG: Record<string, { label: string; color: string; bg: string; icon: any }> = {
    top_performer: { label: 'Top Performer', color: '#16A34A', bg: '#dcfce7', icon: <FiStar /> },
    performant: { label: 'Performant', color: '#3B82F6', bg: '#dbeafe', icon: <FiTrendingUp /> },
    moyen: { label: 'Moyen', color: '#F59E0B', bg: '#fef3c7', icon: <FiActivity /> },
    sous_performant: { label: 'Sous-performant', color: '#EF4444', bg: '#fee2e2', icon: <FiAlertCircle /> },
};

const Analytics: React.FC = () => {
    const [kpi, setKpi] = useState<any>(null);
    const [predictions, setPredictions] = useState<any>(null);
    const [risques, setRisques] = useState<any[]>([]);
    const [recommandations, setRecommandations] = useState<any[]>([]);
    const [tendances, setTendances] = useState<any>(null);
    const [classement, setClassement] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const [overview, setOverview] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [predEnriched, setPredEnriched] = useState<any>(null);
    const [predLoading, setPredLoading] = useState(false);
    const [lastRefresh, setLastRefresh] = useState<string>('');
    const [tab, setTab] = useState<'classement' | 'overview' | 'predictions' | 'risques' | 'recommandations'>('classement');
    const [periode, setPeriode] = useState('mois');
    const [searchRanking, setSearchRanking] = useState('');
    const [catFilter, setCatFilter] = useState('');
    const [equipesList, setEquipesList] = useState<any[]>([]);
    const [filterEquipe, setFilterEquipe] = useState('');

    const loadAllData = async () => {
        try {
            const [k, p, r, rec, t, ov] = await Promise.all([
                getAnalyticsKpi(), getPredictions(), getRisques(), getRecommandations(), getTendances(), getAnalyticsOverview()
            ]);
            setKpi(k.data); setPredictions(p.data); setRisques(r.data); setRecommandations(rec.data); setTendances(t.data); setOverview(ov.data);
        } catch (e) { console.error(e); }
    };

    const loadPredEnriched = async () => {
        setPredLoading(true);
        try {
            const res = await getPredictionsEnriched();
            setPredEnriched(res.data);
            setLastRefresh(new Date().toLocaleString('fr-FR'));
        } catch (e) { console.error(e); }
        setPredLoading(false);
    };

    const recalculateAll = async () => {
        setRefreshing(true);
        await Promise.all([loadAllData(), loadPredEnriched()]);
        // Refresh classement too
        try {
            const res = await getAnalyticsClassement({ periode });
            setClassement(res.data.classement || []);
            setSummary(res.data.summary || null);
        } catch (e) { console.error(e); }
        setRefreshing(false);
        setLastRefresh(new Date().toLocaleString('fr-FR'));
    };

    useEffect(() => {
        loadAllData().finally(() => setLoading(false));
        loadPredEnriched();
        getEquipes().then(res => setEquipesList(res.data)).catch(() => {});
    }, []);

    useEffect(() => {
        getAnalyticsClassement({ periode, equipe_id: filterEquipe || undefined }).then(res => {
            setClassement(res.data.classement || []);
            setSummary(res.data.summary || null);
        }).catch(console.error);
    }, [periode, filterEquipe]);

    const trendIcon = (t: number) => t > 0
        ? <span style={{ color: '#16A34A', fontSize: 12, fontWeight: 600 }}><FiChevronUp size={12} /> +{t}%</span>
        : <span style={{ color: '#EF4444', fontSize: 12, fontWeight: 600 }}><FiChevronDown size={12} /> {t}%</span>;

    const filteredClassement = classement.filter(c => {
        const matchSearch = !searchRanking || c.nom.toLowerCase().includes(searchRanking.toLowerCase());
        const matchCat = !catFilter || c.categorie === catFilter;
        return matchSearch && matchCat;
    });

    const top5 = classement.slice(0, 5);
    const flop5 = [...classement].reverse().slice(0, 5);

    if (loading) return <div className="loading">Chargement...</div>;

    // Charts
    const heureOpts: ApexCharts.ApexOptions = {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'DM Sans' },
        plotOptions: { bar: { borderRadius: 6, columnWidth: '55%' } },
        colors: ['#16A34A'], xaxis: { categories: tendances?.par_heure?.map((h: any) => h.heure + 'h') || [] },
        yaxis: { labels: { formatter: (v: number) => Math.round(v).toString() } },
        grid: { borderColor: '#E5E7EB', strokeDashArray: 4 }, dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v: number) => `${v} activites` } },
    };
    const heureSeries = [{ name: 'Activites', data: tendances?.par_heure?.map((h: any) => h.nb || 0) || [] }];

    const jourData = tendances?.par_jour_semaine?.map((d: any) => ({ ...d, jour_label: JOURS[d.jour - 1] })) || [];
    const jourOpts: ApexCharts.ApexOptions = {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'DM Sans' },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
        colors: ['#8B5CF6', '#3B82F6'],
        xaxis: { categories: jourData.map((d: any) => d.jour_label) },
        yaxis: [
            { title: { text: 'Activites' }, labels: { formatter: (v: number) => Math.round(v).toString() } },
            { opposite: true, title: { text: 'CA (F)' }, labels: { formatter: (v: number) => v >= 1000 ? Math.round(v / 1000) + 'K' : v.toString() } }
        ],
        legend: { position: 'top' }, tooltip: { shared: true, intersect: false },
        grid: { borderColor: '#E5E7EB', strokeDashArray: 4 }, dataLabels: { enabled: false },
    };
    const jourSeries = [
        { name: 'Activites', data: jourData.map((d: any) => d.nb || 0) },
        { name: 'CA (F)', data: jourData.map((d: any) => d.ca || 0) }
    ];

    // Score distribution chart
    const scoreDistData = [
        summary?.top_performers || 0, summary?.performants || 0,
        summary?.moyens || 0, summary?.sous_performants || 0
    ];
    const scoreDistOpts: ApexCharts.ApexOptions = {
        chart: { type: 'donut', fontFamily: 'DM Sans' },
        labels: ['Top Performers', 'Performants', 'Moyens', 'Sous-performants'],
        colors: ['#16A34A', '#3B82F6', '#F59E0B', '#EF4444'],
        legend: { position: 'bottom', fontSize: '12px' },
        plotOptions: { pie: { donut: { size: '60%', labels: { show: true, total: { show: true, label: 'Total', formatter: () => String(summary?.total || 0) } } } } },
        dataLabels: { enabled: true, formatter: (val: number) => Math.round(val) + '%' },
    };

    // Prediction chart
    const predictionData = [
        ...(predictions?.historique || []).map((h: any) => ({ label: `${h.mois}/${h.annee}`, ca: h.ca })),
        ...(predictions?.predictions || []).map((p: any) => ({ label: `${p.mois}/${p.annee}`, ca: p.ca_predit }))
    ];
    const historLen = predictions?.historique?.length || 0;
    const predLineOpts: ApexCharts.ApexOptions = {
        chart: { type: 'line', toolbar: { show: true }, fontFamily: 'DM Sans', zoom: { enabled: true } },
        stroke: { width: 3, curve: 'smooth' }, colors: ['#16A34A'],
        xaxis: { categories: predictionData.map(d => d.label) },
        yaxis: { labels: { formatter: (v: number) => v >= 1000000 ? (v / 1000000).toFixed(1) + 'M' : v >= 1000 ? Math.round(v / 1000) + 'K' : v.toString() } },
        grid: { borderColor: '#E5E7EB', strokeDashArray: 4 },
        tooltip: { y: { formatter: (v: number) => `${v.toLocaleString()} F` } },
        annotations: { xaxis: historLen > 0 ? [{ x: predictionData[historLen - 1]?.label, borderColor: '#F59E0B', label: { text: 'Predictions →', style: { color: '#F59E0B', background: 'transparent' } } }] : [] },
        markers: { size: 4 },
    };
    const predLineSeries = [{ name: 'CA (F)', data: predictionData.map(d => d.ca || 0) }];

    return (
        <div className="page-analytics">
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><FiTrendingUp /> Analytics & Intelligence</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {lastRefresh && <span style={{ fontSize: 11, color: '#94A3B8' }}>Mis a jour: {lastRefresh}</span>}
                    <button
                        className="btn btn-primary"
                        onClick={recalculateAll}
                        disabled={refreshing}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', fontSize: 13 }}
                    >
                        <FiRefreshCw size={15} className={refreshing ? 'spin' : ''} />
                        {refreshing ? 'Recalcul...' : 'Recalculer tout'}
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="tabs" style={{ marginBottom: 20 }}>
                <button className={`tab ${tab === 'classement' ? 'active' : ''}`} onClick={() => setTab('classement')}>
                    <FiAward size={13} /> Classement
                </button>
                <button className={`tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>
                    <FiActivity size={13} /> Vue d'ensemble
                </button>
                <button className={`tab ${tab === 'predictions' ? 'active' : ''}`} onClick={() => setTab('predictions')}>
                    <FiTarget size={13} /> Predictions
                </button>
                <button className={`tab ${tab === 'risques' ? 'active' : ''}`} onClick={() => setTab('risques')}>
                    <FiAlertTriangle size={13} /> Risques ({risques.filter(r => r.niveau === 'eleve').length})
                </button>
                <button className={`tab ${tab === 'recommandations' ? 'active' : ''}`} onClick={() => setTab('recommandations')}>
                    <FiZap size={13} /> Recommandations ({recommandations.length})
                </button>
            </div>

            {/* ═══ CLASSEMENT TAB ═══ */}
            {tab === 'classement' && (
                <>
                    {/* Equipe filter */}
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
                        <select value={filterEquipe} onChange={e => setFilterEquipe(e.target.value)}
                            style={{ padding: '6px 12px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13 }}>
                            <option value="">Toutes les equipes</option>
                            {equipesList.map(eq => <option key={eq.id} value={eq.id}>{eq.nom}</option>)}
                        </select>
                        {filterEquipe && <button className="btn btn-outline btn-sm" onClick={() => setFilterEquipe('')} style={{ fontSize: 12 }}>Reinitialiser</button>}
                    </div>
                    {/* Summary cards */}
                    {summary && (
                        <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
                            <div className="kpi-card" style={{ borderLeftColor: '#16A34A' }}>
                                <div className="kpi-content">
                                    <span className="kpi-value">{summary.total}</span>
                                    <span className="kpi-label">Commerciaux actifs</span>
                                </div>
                            </div>
                            <div className="kpi-card" style={{ borderLeftColor: '#3B82F6' }}>
                                <div className="kpi-content">
                                    <span className="kpi-value">{(summary.ca_total || 0).toLocaleString()} F</span>
                                    <span className="kpi-label">CA Total</span>
                                </div>
                            </div>
                            <div className="kpi-card" style={{ borderLeftColor: '#8B5CF6' }}>
                                <div className="kpi-content">
                                    <span className="kpi-value">{summary.activites_total}</span>
                                    <span className="kpi-label">Activites Total</span>
                                </div>
                            </div>
                            <div className="kpi-card" style={{ borderLeftColor: '#F59E0B' }}>
                                <div className="kpi-content">
                                    <span className="kpi-value">{summary.score_moyen}/100</span>
                                    <span className="kpi-label">Score moyen</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TOP 5 / FLOP 5 + Distribution */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
                        {/* TOP 5 */}
                        <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
                            <h3 style={{ margin: '0 0 16px', fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <FiChevronUp style={{ color: '#16A34A' }} /> TOP 5
                            </h3>
                            {top5.map((c, i) => (
                                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < 4 ? '1px solid #f3f4f6' : 'none' }}>
                                    <span style={{
                                        width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 700, fontSize: 13,
                                        background: i === 0 ? '#FEF3C7' : i === 1 ? '#F3F4F6' : i === 2 ? '#FED7AA' : '#f9fafb',
                                        color: i === 0 ? '#B45309' : i === 1 ? '#6B7280' : i === 2 ? '#C2410C' : '#999'
                                    }}>
                                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : c.rang}
                                    </span>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600, fontSize: 13 }}>{c.nom}</div>
                                        <div style={{ fontSize: 11, color: '#999' }}>{c.nb_activites} act. | {c.clients_visites} clients</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontWeight: 700, color: '#16A34A', fontSize: 13 }}>{c.ca.toLocaleString()} F</div>
                                        <div style={{ fontSize: 11, color: '#999' }}>Score: {c.score}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* FLOP 5 */}
                        <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
                            <h3 style={{ margin: '0 0 16px', fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <FiChevronDown style={{ color: '#EF4444' }} /> FLOP 5
                            </h3>
                            {flop5.map((c, i) => (
                                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < 4 ? '1px solid #f3f4f6' : 'none' }}>
                                    <span style={{
                                        width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 700, fontSize: 12, background: '#FEE2E2', color: '#EF4444'
                                    }}>
                                        {c.rang}
                                    </span>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600, fontSize: 13 }}>{c.nom}</div>
                                        <div style={{ fontSize: 11, color: '#999' }}>{c.nb_activites} act. | {c.jours_actifs}j actifs</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontWeight: 700, color: '#EF4444', fontSize: 13 }}>{c.ca.toLocaleString()} F</div>
                                        <div style={{ fontSize: 11, color: '#999' }}>Score: {c.score}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Distribution donut */}
                        <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e5e7eb' }}>
                            <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>Repartition</h3>
                            <Chart options={scoreDistOpts} series={scoreDistData} type="donut" height={240} />
                        </div>
                    </div>

                    {/* Filters */}
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
                        <div className="search-box" style={{ flex: 1, minWidth: 200, maxWidth: 300 }}>
                            <FiSearch />
                            <input placeholder="Rechercher un commercial..." value={searchRanking} onChange={e => setSearchRanking(e.target.value)} />
                        </div>
                        <select value={periode} onChange={e => setPeriode(e.target.value)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
                            <option value="semaine">Cette semaine</option>
                            <option value="mois">Ce mois</option>
                            <option value="trimestre">Ce trimestre</option>
                            <option value="annee">Cette annee</option>
                        </select>
                        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }}>
                            <option value="">Toutes categories</option>
                            <option value="top_performer">Top Performers</option>
                            <option value="performant">Performants</option>
                            <option value="moyen">Moyens</option>
                            <option value="sous_performant">Sous-performants</option>
                        </select>
                        <span style={{ fontSize: 13, color: '#999' }}>{filteredClassement.length} commercial{filteredClassement.length !== 1 ? 'x' : ''}</span>
                    </div>

                    {/* Full ranking table */}
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 50 }}>#</th>
                                    <th>Commercial</th>
                                    <th>Score</th>
                                    <th>Categorie</th>
                                    <th>Activites</th>
                                    <th>CA</th>
                                    <th>Clients</th>
                                    <th>Jours</th>
                                    <th>Conversion</th>
                                    <th>Objectifs</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredClassement.map(c => {
                                    const cat = CAT_CONFIG[c.categorie] || CAT_CONFIG.moyen;
                                    return (
                                        <tr key={c.id}>
                                            <td>
                                                <span style={{
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    width: 28, height: 28, borderRadius: '50%', fontWeight: 700, fontSize: 12,
                                                    background: c.rang <= 3 ? '#dcfce7' : c.rang > classement.length - 3 ? '#fee2e2' : '#f3f4f6',
                                                    color: c.rang <= 3 ? '#16A34A' : c.rang > classement.length - 3 ? '#EF4444' : '#666'
                                                }}>
                                                    {c.rang}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <span style={{
                                                        width: 32, height: 32, borderRadius: '50%', background: '#e5e7eb',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: 11, fontWeight: 700, color: '#666'
                                                    }}>
                                                        {c.nom.split(' ').map((w: string) => w[0]).join('').substring(0, 2).toUpperCase()}
                                                    </span>
                                                    <strong style={{ fontSize: 13 }}>{c.nom}</strong>
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                    <div style={{ width: 50, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                                                        <div style={{ width: `${c.score}%`, height: '100%', background: cat.color, borderRadius: 3 }} />
                                                    </div>
                                                    <span style={{ fontSize: 12, fontWeight: 700, color: cat.color }}>{c.score}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                                    padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                                                    background: cat.bg, color: cat.color
                                                }}>
                                                    {cat.icon} {cat.label}
                                                </span>
                                            </td>
                                            <td style={{ fontWeight: 600 }}>{c.nb_activites}</td>
                                            <td style={{ fontWeight: 700, color: '#16A34A' }}>{c.ca.toLocaleString()} F</td>
                                            <td>{c.clients_visites}</td>
                                            <td>{c.jours_actifs}</td>
                                            <td>
                                                <span style={{ color: c.taux_conversion >= 30 ? '#16A34A' : c.taux_conversion >= 15 ? '#F59E0B' : '#EF4444', fontWeight: 600 }}>
                                                    {c.taux_conversion}%
                                                </span>
                                            </td>
                                            <td>
                                                <span style={{ fontSize: 12 }}>
                                                    {c.obj_atteints}/{c.obj_total}
                                                    <span style={{ color: '#999', marginLeft: 4 }}>({c.taux_objectif}%)</span>
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* ═══ OVERVIEW TAB ═══ */}
            {tab === 'overview' && kpi && (
                <>
                    {/* KPI Cards */}
                    <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
                        <div className="kpi-card" style={{ borderLeftColor: '#16A34A' }}>
                            <div className="kpi-content">
                                <span className="kpi-value">{kpi.activites?.valeur || 0}</span>
                                <span className="kpi-label">Activites ce mois {trendIcon(kpi.activites?.tendance)}</span>
                            </div>
                        </div>
                        <div className="kpi-card" style={{ borderLeftColor: '#3B82F6' }}>
                            <div className="kpi-content">
                                <span className="kpi-value">{Math.round(kpi.ca?.valeur || 0).toLocaleString()} F</span>
                                <span className="kpi-label">Chiffre d'affaires {trendIcon(kpi.ca?.tendance)}</span>
                            </div>
                        </div>
                        <div className="kpi-card" style={{ borderLeftColor: '#F59E0B' }}>
                            <div className="kpi-content">
                                <span className="kpi-value">{kpi.commerciaux_actifs?.valeur || 0}</span>
                                <span className="kpi-label">Commerciaux actifs {trendIcon(kpi.commerciaux_actifs?.tendance)}</span>
                            </div>
                        </div>
                        <div className="kpi-card" style={{ borderLeftColor: '#8B5CF6' }}>
                            <div className="kpi-content">
                                <span className="kpi-value">{kpi.clients_touches?.valeur || 0}</span>
                                <span className="kpi-label">Clients touches {trendIcon(kpi.clients_touches?.tendance)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Row 1: Evolution 30j + CA mensuel */}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 20 }}>
                        {/* Evolution 30 days */}
                        {overview?.evolution && (
                            <div className="chart-card">
                                <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Evolution 30 derniers jours</h3>
                                <Chart
                                    options={{
                                        chart: { type: 'area', toolbar: { show: false }, fontFamily: 'DM Sans', sparkline: { enabled: false } },
                                        stroke: { width: 2, curve: 'smooth' },
                                        colors: ['#16A34A', '#3B82F6'],
                                        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05 } },
                                        xaxis: { categories: overview.evolution.map((e: any) => new Date(e.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })), labels: { rotate: -45, style: { fontSize: '10px' } }, tickAmount: 10 },
                                        yaxis: [
                                            { title: { text: 'Activites', style: { fontSize: '11px' } }, labels: { formatter: (v: number) => Math.round(v).toString() } },
                                            { opposite: true, title: { text: 'CA (F)', style: { fontSize: '11px' } }, labels: { formatter: (v: number) => v >= 1000 ? Math.round(v / 1000) + 'K' : v.toString() } }
                                        ],
                                        grid: { borderColor: '#E5E7EB', strokeDashArray: 4 },
                                        dataLabels: { enabled: false }, legend: { position: 'top' },
                                        tooltip: { shared: true, y: { formatter: (v: number, opts: any) => opts.seriesIndex === 1 ? v.toLocaleString() + ' F' : v + ' act.' } },
                                    }}
                                    series={[
                                        { name: 'Activites', data: overview.evolution.map((e: any) => e.nb) },
                                        { name: 'CA', data: overview.evolution.map((e: any) => Number(e.ca)) }
                                    ]}
                                    type="area" height={280}
                                />
                            </div>
                        )}

                        {/* Conversion funnel */}
                        {overview?.funnel && (
                            <div className="chart-card">
                                <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>Entonnoir de conversion</h3>
                                {[
                                    { label: 'Visites', value: overview.funnel.visites, color: '#3B82F6' },
                                    { label: 'Ventes', value: overview.funnel.ventes, color: '#16A34A' },
                                    { label: 'Commandes', value: overview.funnel.commandes, color: '#F59E0B' },
                                    { label: 'Installations', value: overview.funnel.installations, color: '#8B5CF6' },
                                ].map((step, i, arr) => {
                                    const max = Math.max(...arr.map(a => a.value), 1);
                                    const pct = Math.round((step.value / max) * 100);
                                    return (
                                        <div key={step.label} style={{ marginBottom: 14 }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                                                <span style={{ fontWeight: 600 }}>{step.label}</span>
                                                <span style={{ fontWeight: 700, color: step.color }}>{step.value}</span>
                                            </div>
                                            <div style={{ height: 10, background: '#f3f4f6', borderRadius: 6, overflow: 'hidden' }}>
                                                <div style={{ width: `${pct}%`, height: '100%', background: step.color, borderRadius: 6, transition: 'width 0.5s' }} />
                                            </div>
                                            {i > 0 && arr[i - 1].value > 0 && (
                                                <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>
                                                    Taux: {Math.round((step.value / arr[i - 1].value) * 100)}%
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Row 2: Type breakdown donut + par heure + par jour semaine */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
                        {/* Activity types donut */}
                        {overview?.par_type && overview.par_type.length > 0 && (
                            <div className="chart-card">
                                <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>Repartition par type</h3>
                                <Chart
                                    options={{
                                        chart: { type: 'donut', fontFamily: 'DM Sans' },
                                        labels: overview.par_type.map((t: any) => t.type),
                                        colors: ['#16A34A', '#3B82F6', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899'],
                                        legend: { position: 'bottom', fontSize: '11px' },
                                        plotOptions: { pie: { donut: { size: '55%' } } },
                                        dataLabels: { enabled: true, formatter: (val: number) => Math.round(val) + '%', style: { fontSize: '11px' } },
                                    }}
                                    series={overview.par_type.map((t: any) => t.nb)}
                                    type="donut" height={240}
                                />
                            </div>
                        )}

                        {/* Par heure */}
                        {tendances?.par_heure && (
                            <div className="chart-card">
                                <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>Activite par heure</h3>
                                <Chart options={heureOpts} series={heureSeries} type="bar" height={240} />
                            </div>
                        )}

                        {/* Par jour semaine */}
                        {tendances?.par_jour_semaine && (
                            <div className="chart-card">
                                <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>Par jour de la semaine</h3>
                                <Chart options={{...jourOpts, legend: { ...jourOpts.legend, fontSize: '10px' }}} series={jourSeries} type="bar" height={240} />
                            </div>
                        )}
                    </div>

                    {/* Row 3: Top zones + Top produits + CA mensuel */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                        {/* Zones performance */}
                        {overview?.par_zone && overview.par_zone.length > 0 && (
                            <div className="chart-card">
                                <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Top zones</h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {overview.par_zone.slice(0, 6).map((z: any, i: number) => {
                                        const maxCa = Math.max(...overview.par_zone.map((zz: any) => Number(zz.ca)), 1);
                                        return (
                                            <div key={i}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                                                    <span style={{ fontWeight: 600 }}>{z.zone}</span>
                                                    <span style={{ color: '#16A34A', fontWeight: 700 }}>{Number(z.ca).toLocaleString()} F</span>
                                                </div>
                                                <div style={{ height: 6, background: '#f3f4f6', borderRadius: 4 }}>
                                                    <div style={{ width: `${(Number(z.ca) / maxCa) * 100}%`, height: '100%', background: i === 0 ? '#16A34A' : i === 1 ? '#22C55E' : '#86EFAC', borderRadius: 4 }} />
                                                </div>
                                                <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>{z.nb_activites} act. | {z.commerciaux} com. | {z.clients} clients</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Top produits */}
                        {overview?.top_produits && overview.top_produits.length > 0 && (
                            <div className="chart-card">
                                <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>Top produits</h3>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {overview.top_produits.slice(0, 6).map((p: any, i: number) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #f3f4f6' }}>
                                            <span style={{
                                                width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center',
                                                justifyContent: 'center', fontSize: 10, fontWeight: 700,
                                                background: i < 3 ? '#dcfce7' : '#f3f4f6', color: i < 3 ? '#16A34A' : '#999'
                                            }}>{i + 1}</span>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.nom}</div>
                                                <div style={{ fontSize: 10, color: '#999' }}>{p.nb_ventes} ventes</div>
                                            </div>
                                            <span style={{ fontSize: 12, fontWeight: 700, color: '#16A34A' }}>{Number(p.ca).toLocaleString()} F</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* CA Mensuel (6 mois) */}
                        {overview?.mensuel && overview.mensuel.length > 0 && (
                            <div className="chart-card">
                                <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>CA mensuel (6 mois)</h3>
                                <Chart
                                    options={{
                                        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'DM Sans' },
                                        plotOptions: { bar: { borderRadius: 6, columnWidth: '55%' } },
                                        colors: ['#3B82F6'],
                                        xaxis: { categories: overview.mensuel.map((m: any) => `${String(m.mois).padStart(2, '0')}/${m.annee}`) },
                                        yaxis: { labels: { formatter: (v: number) => v >= 1000000 ? (v / 1000000).toFixed(1) + 'M' : v >= 1000 ? Math.round(v / 1000) + 'K' : v.toString() } },
                                        grid: { borderColor: '#E5E7EB', strokeDashArray: 4 },
                                        dataLabels: { enabled: false },
                                        tooltip: { y: { formatter: (v: number) => v.toLocaleString() + ' F' } },
                                    }}
                                    series={[{ name: 'CA', data: overview.mensuel.map((m: any) => Number(m.ca)) }]}
                                    type="bar" height={240}
                                />
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* ═══ PREDICTIONS TAB ═══ */}
            {tab === 'predictions' && (
                <div>
                    {predLoading && !predEnriched ? (
                        <div style={{ padding: 60, textAlign: 'center', color: '#94A3B8' }}>
                            <div className="spinner" style={{ margin: '0 auto 12px' }} />
                            Calcul des predictions...
                        </div>
                    ) : (
                        <>
                            {/* Trend indicators */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
                                {[
                                    { label: 'Tendance CA', value: predEnriched?.tendance_ca || predictions?.tendance || '--', icon: <FiDollarSign />, isUp: (predEnriched?.tendance_ca || predictions?.tendance) === 'hausse' },
                                    { label: 'Tendance Activites', value: predEnriched?.tendance_activites || '--', icon: <FiActivity />, isUp: predEnriched?.tendance_activites === 'hausse' },
                                    { label: 'CA moyen/mois', value: predEnriched?.moyennes?.ca ? `${predEnriched.moyennes.ca.toLocaleString()} F` : '--', icon: <FiBarChart2 />, isUp: true },
                                    { label: 'Activites moy./mois', value: predEnriched?.moyennes?.activites || '--', icon: <FiCalendar />, isUp: true },
                                ].map((item, i) => (
                                    <div key={i} style={{
                                        background: '#fff', borderRadius: 14, padding: '18px 20px',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #E2E8F0',
                                        display: 'flex', alignItems: 'center', gap: 14
                                    }}>
                                        <div style={{
                                            width: 44, height: 44, borderRadius: 12,
                                            background: item.isUp ? '#F0FDF4' : '#FEF2F2',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: item.isUp ? '#16A34A' : '#EF4444', fontSize: 20
                                        }}>{item.icon}</div>
                                        <div>
                                            <div style={{ fontSize: 18, fontWeight: 800, color: '#1E293B', textTransform: 'capitalize' }}>{item.value}</div>
                                            <div style={{ fontSize: 12, color: '#64748B', fontWeight: 600 }}>{item.label}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Main prediction chart + cards row */}
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 20 }}>
                                <div className="chart-card" style={{ background: '#fff', borderRadius: 14, padding: 24, border: '1px solid #E2E8F0' }}>
                                    <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700 }}>Previsions CA (3 prochains mois)</h3>
                                    {predictions && <Chart options={predLineOpts} series={predLineSeries} type="line" height={300} />}
                                </div>

                                {/* Prediction cards */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {(predEnriched?.predictions || predictions?.predictions || []).map((p: any, i: number) => {
                                        const conf = Math.round((p.confiance || 0.8) * 100);
                                        return (
                                            <div key={i} style={{
                                                background: '#fff', borderRadius: 14, padding: '20px 18px', border: '1px solid #E2E8F0',
                                                position: 'relative', overflow: 'hidden'
                                            }}>
                                                <div style={{
                                                    position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                                                    background: `linear-gradient(90deg, #16A34A ${conf}%, #E2E8F0 ${conf}%)`
                                                }} />
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                    <span style={{ fontSize: 13, color: '#64748B', fontWeight: 600 }}>
                                                        <FiCalendar size={12} /> {p.mois}/{p.annee}
                                                    </span>
                                                    <span style={{
                                                        padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700,
                                                        background: conf >= 80 ? '#F0FDF4' : conf >= 60 ? '#FFF7ED' : '#FEF2F2',
                                                        color: conf >= 80 ? '#16A34A' : conf >= 60 ? '#F59E0B' : '#EF4444'
                                                    }}>
                                                        {conf}% confiance
                                                    </span>
                                                </div>
                                                <div style={{ fontSize: 24, fontWeight: 800, color: '#16A34A', marginBottom: 6 }}>
                                                    {(p.ca_predit || 0).toLocaleString()} F
                                                </div>
                                                {p.activites_predit && (
                                                    <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#64748B' }}>
                                                        <span>{p.activites_predit} activites</span>
                                                        <span>{p.ventes_predit} ventes</span>
                                                        <span>{p.clients_predit} clients</span>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Row 2: Growth rates + Best/Worst + Objectifs forecast */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
                                {/* Growth chart */}
                                {predEnriched?.croissance && predEnriched.croissance.length > 0 && (
                                    <div style={{ background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #E2E8F0' }}>
                                        <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <FiTrendingUp size={16} /> Croissance mensuelle
                                        </h3>
                                        <Chart
                                            options={{
                                                chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'DM Sans' },
                                                plotOptions: { bar: { borderRadius: 6, columnWidth: '55%', colors: { ranges: [{ from: -100, to: 0, color: '#EF4444' }, { from: 0, to: 1000, color: '#16A34A' }] } } },
                                                xaxis: { categories: predEnriched.croissance.map((c: any) => `${c.mois}/${c.annee}`) },
                                                yaxis: { labels: { formatter: (v: number) => Math.round(v) + '%' } },
                                                grid: { borderColor: '#E5E7EB', strokeDashArray: 4 },
                                                dataLabels: { enabled: true, formatter: (v: number) => Math.round(v) + '%', style: { fontSize: '11px' } },
                                                tooltip: { y: { formatter: (v: number) => v + '%' } },
                                            }}
                                            series={[{ name: 'Croissance CA', data: predEnriched.croissance.map((c: any) => c.ca_growth) }]}
                                            type="bar" height={220}
                                        />
                                    </div>
                                )}

                                {/* Best/Worst month + Averages */}
                                <div style={{ background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #E2E8F0' }}>
                                    <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <FiStar size={16} /> Points cles
                                    </h3>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        {predEnriched?.meilleur_mois && (
                                            <div style={{ padding: '12px 14px', background: '#F0FDF4', borderRadius: 10, border: '1px solid #BBF7D0' }}>
                                                <div style={{ fontSize: 11, color: '#16A34A', fontWeight: 700, marginBottom: 4 }}>Meilleur mois</div>
                                                <div style={{ fontSize: 16, fontWeight: 800, color: '#14532D' }}>
                                                    {Number(predEnriched.meilleur_mois.ca).toLocaleString()} F
                                                </div>
                                                <div style={{ fontSize: 12, color: '#64748B' }}>
                                                    {predEnriched.meilleur_mois.mois}/{predEnriched.meilleur_mois.annee} - {predEnriched.meilleur_mois.nb_activites} activites
                                                </div>
                                            </div>
                                        )}
                                        {predEnriched?.pire_mois && (
                                            <div style={{ padding: '12px 14px', background: '#FEF2F2', borderRadius: 10, border: '1px solid #FECACA' }}>
                                                <div style={{ fontSize: 11, color: '#EF4444', fontWeight: 700, marginBottom: 4 }}>Mois le plus faible</div>
                                                <div style={{ fontSize: 16, fontWeight: 800, color: '#7F1D1D' }}>
                                                    {Number(predEnriched.pire_mois.ca).toLocaleString()} F
                                                </div>
                                                <div style={{ fontSize: 12, color: '#64748B' }}>
                                                    {predEnriched.pire_mois.mois}/{predEnriched.pire_mois.annee} - {predEnriched.pire_mois.nb_activites} activites
                                                </div>
                                            </div>
                                        )}
                                        {predEnriched?.moyennes && (
                                            <div style={{ padding: '12px 14px', background: '#EFF6FF', borderRadius: 10, border: '1px solid #BFDBFE' }}>
                                                <div style={{ fontSize: 11, color: '#3B82F6', fontWeight: 700, marginBottom: 6 }}>Moyennes mensuelles</div>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12 }}>
                                                    <div><span style={{ color: '#94A3B8' }}>Ventes:</span> <strong>{predEnriched.moyennes.ventes}</strong></div>
                                                    <div><span style={{ color: '#94A3B8' }}>Clients:</span> <strong>{predEnriched.moyennes.clients}</strong></div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Objectifs forecast */}
                                {predEnriched?.objectifs_forecast && (
                                    <div style={{ background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #E2E8F0' }}>
                                        <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <FiTarget size={16} /> Prevision Objectifs
                                        </h3>
                                        <div style={{ textAlign: 'center', marginBottom: 16 }}>
                                            <div style={{ fontSize: 40, fontWeight: 800, color: '#3B82F6' }}>
                                                {Math.round(predEnriched.objectifs_forecast.taux_moyen || 0)}%
                                            </div>
                                            <div style={{ fontSize: 13, color: '#64748B' }}>Taux d'atteinte moyen</div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                            {[
                                                { label: 'Atteints', value: predEnriched.objectifs_forecast.atteints || 0, color: '#16A34A', bg: '#F0FDF4', icon: <FiCheckCircle size={14} /> },
                                                { label: 'En bonne voie', value: predEnriched.objectifs_forecast.en_bonne_voie || 0, color: '#3B82F6', bg: '#EFF6FF', icon: <FiTrendingUp size={14} /> },
                                                { label: 'En retard', value: predEnriched.objectifs_forecast.en_retard || 0, color: '#EF4444', bg: '#FEF2F2', icon: <FiAlertTriangle size={14} /> },
                                            ].map((item, i) => (
                                                <div key={i} style={{
                                                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                                                    background: item.bg, borderRadius: 10
                                                }}>
                                                    <span style={{ color: item.color }}>{item.icon}</span>
                                                    <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{item.label}</span>
                                                    <span style={{ fontSize: 18, fontWeight: 800, color: item.color }}>{item.value}</span>
                                                </div>
                                            ))}
                                            <div style={{
                                                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                                                background: '#F8FAFC', borderRadius: 10, borderTop: '1px solid #E2E8F0'
                                            }}>
                                                <FiTarget size={14} style={{ color: '#64748B' }} />
                                                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#64748B' }}>Total actifs</span>
                                                <span style={{ fontSize: 18, fontWeight: 800, color: '#374151' }}>{predEnriched.objectifs_forecast.total || 0}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Row 3: Top commerciaux performance trend */}
                            {predEnriched?.top_commerciaux && predEnriched.top_commerciaux.length > 0 && (
                                <div style={{ background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #E2E8F0' }}>
                                    <h3 style={{ margin: '0 0 14px', fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <FiUsers size={16} /> Tendance Top 10 Commerciaux (30 derniers jours)
                                    </h3>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                                                    <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748B' }}>Commercial</th>
                                                    <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#64748B' }}>CA 30j</th>
                                                    <th style={{ padding: '10px 14px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#64748B' }}>Evol. CA</th>
                                                    <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#64748B' }}>Activites</th>
                                                    <th style={{ padding: '10px 14px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#64748B' }}>Evol. Act.</th>
                                                    <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#64748B' }}>Clients</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {predEnriched.top_commerciaux.map((c: any, i: number) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                                                        <td style={{ padding: '10px 14px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                <span style={{
                                                                    width: 28, height: 28, borderRadius: 8,
                                                                    background: i < 3 ? '#F0FDF4' : '#F8FAFC',
                                                                    color: i < 3 ? '#16A34A' : '#94A3B8',
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    fontSize: 12, fontWeight: 700
                                                                }}>{i + 1}</span>
                                                                <span style={{ fontSize: 13, fontWeight: 600 }}>{c.nom}</span>
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, fontSize: 13, color: '#16A34A' }}>
                                                            {c.ca_30j.toLocaleString()} F
                                                        </td>
                                                        <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                                            <span style={{
                                                                padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                                                                background: c.tendance_ca > 0 ? '#F0FDF4' : c.tendance_ca < 0 ? '#FEF2F2' : '#F8FAFC',
                                                                color: c.tendance_ca > 0 ? '#16A34A' : c.tendance_ca < 0 ? '#EF4444' : '#94A3B8'
                                                            }}>
                                                                {c.tendance_ca > 0 ? '+' : ''}{c.tendance_ca}%
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, fontSize: 13 }}>
                                                            {c.activites_30j}
                                                        </td>
                                                        <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                                                            <span style={{
                                                                padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                                                                background: c.tendance_act > 0 ? '#F0FDF4' : c.tendance_act < 0 ? '#FEF2F2' : '#F8FAFC',
                                                                color: c.tendance_act > 0 ? '#16A34A' : c.tendance_act < 0 ? '#EF4444' : '#94A3B8'
                                                            }}>
                                                                {c.tendance_act > 0 ? '+' : ''}{c.tendance_act}%
                                                            </span>
                                                        </td>
                                                        <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: 13, fontWeight: 600 }}>
                                                            {c.clients_30j}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* ═══ RISQUES TAB ═══ */}
            {tab === 'risques' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {risques.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucun risque detecte</div>
                    ) : risques.map((r, i) => (
                        <div key={i} style={{
                            background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e5e7eb',
                            borderLeft: `4px solid ${r.niveau === 'eleve' ? '#EF4444' : r.niveau === 'moyen' ? '#F59E0B' : '#9CA3AF'}`
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                <FiAlertTriangle color={r.niveau === 'eleve' ? '#EF4444' : r.niveau === 'moyen' ? '#F59E0B' : '#9CA3AF'} />
                                <strong style={{ fontSize: 14 }}>{r.nom}</strong>
                                <span style={{
                                    padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                                    background: r.niveau === 'eleve' ? '#FEE2E2' : r.niveau === 'moyen' ? '#FEF3C7' : '#F3F4F6',
                                    color: r.niveau === 'eleve' ? '#EF4444' : r.niveau === 'moyen' ? '#F59E0B' : '#6B7280'
                                }}>
                                    Risque {r.niveau} ({r.score_risque}%)
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#666', marginBottom: 6 }}>
                                <span>Activites: {r.activites_mois}</span>
                                <span>CA: {(r.ca_mois || 0).toLocaleString()} F</span>
                                <span>Objectifs: {Math.round(r.taux_objectif_moyen || 0)}%</span>
                            </div>
                            {r.raisons?.length > 0 && (
                                <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12, color: '#999' }}>
                                    {r.raisons.map((ra: string, j: number) => <li key={j}>{ra}</li>)}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* ═══ RECOMMANDATIONS TAB ═══ */}
            {tab === 'recommandations' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {recommandations.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucune recommandation</div>
                    ) : recommandations.map((r, i) => (
                        <div key={i} style={{
                            background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e5e7eb',
                            display: 'flex', gap: 12, alignItems: 'flex-start',
                            borderLeft: `4px solid ${r.priorite === 'haute' ? '#EF4444' : r.priorite === 'moyenne' ? '#F59E0B' : '#16A34A'}`
                        }}>
                            <FiZap size={18} style={{ flexShrink: 0, marginTop: 2 }} color={r.priorite === 'haute' ? '#EF4444' : r.priorite === 'moyenne' ? '#F59E0B' : '#16A34A'} />
                            <div>
                                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                                    <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600, background: '#E5E7EB', color: '#374151' }}>{r.type}</span>
                                    <span style={{
                                        padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 600,
                                        background: r.priorite === 'haute' ? '#FEE2E2' : r.priorite === 'moyenne' ? '#FEF3C7' : '#DCFCE7',
                                        color: r.priorite === 'haute' ? '#EF4444' : r.priorite === 'moyenne' ? '#F59E0B' : '#16A34A'
                                    }}>{r.priorite}</span>
                                </div>
                                <p style={{ margin: 0, fontSize: 13, color: '#374151' }}>{r.message}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Analytics;
